import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PrivacyBlock } from "@/components/PrivacyBlock";
import { Car, Fuel, Gauge, Settings2, Calendar, Palette, Shield, ChevronLeft,
  Phone, MessageSquare, MapPin, Clock, Heart, Download, ArrowRight,
  CheckCircle, Zap, DoorOpen, Users, Cog, Leaf, Package, Star,
  FileText, Send, X, ZoomIn, ChevronRight,
} from "lucide-react";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

const ECO_COLORS = { "0": "bg-blue-500", "eco": "bg-teal-500", "c": "bg-green-500", "b": "bg-yellow-500", "none": "bg-slate-400" };
const ECO_LABELS = { "0": "Cero emisiones", "eco": "ECO", "c": "Etiqueta C", "b": "Etiqueta B", "none": "Sin etiqueta" };
const STOCK_LABELS = { "new": "Nuevo", "km0": "KM0", "used": "Ocasion", "nearly_new": "Seminuevo", "usado": "Ocasion" };
const STOCK_COLORS = { "new": "bg-emerald-600", "km0": "bg-red-600", "used": "bg-slate-700", "nearly_new": "bg-amber-600", "usado": "bg-slate-700" };

const EQUIP_CATEGORIES = {
  "Confort": ["Climatizador", "Asientos calefactados", "Asiento conductor regulable", "Volante multifuncion", "Llave inteligente", "Arranque sin llave", "Espejo interior antideslumbrante", "Retrovisores electricos", "Elevalunas electricos"],
  "Seguridad": ["ABS", "ESP", "Airbag", "Control crucero", "Sensor lluvia", "Sensor aparcamiento", "Camara trasera", "Frenada autonoma", "Alerta angulo muerto"],
  "Conectividad": ["Pantalla tactil", "Apple CarPlay", "Android Auto", "Bluetooth", "USB", "DAB", "Navegador", "Cargador inalambrico"],
  "Ayudas conduccion": ["Control crucero adaptativo", "Mantenimiento carril", "Reconocimiento senales", "Asistente arranque cuesta", "Luces automaticas"],
  "Exterior": ["Llantas aleacion", "Barras techo", "LED", "Faros antiniebla", "Cristales tintados", "Pintura metalizada"],
  "Interior": ["Tapiceria tela", "Tapiceria cuero", "Maletero regulable", "Asientos abatibles", "Reposabrazos central"]
};

function categorizeEquipment(items) {
  const result = {};
  const uncategorized = [];
  for (const item of items) {
    let found = false;
    for (const [cat, keywords] of Object.entries(EQUIP_CATEGORIES)) {
      if (keywords.some(k => item.toLowerCase().includes(k.toLowerCase()))) {
        if (!result[cat]) result[cat] = [];
        result[cat].push(item);
        found = true;
        break;
      }
    }
    if (!found) uncategorized.push(item);
  }
  if (uncategorized.length) result["Otros"] = uncategorized;
  return result;
}

export default function VehicleDetailPage() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const [privacy, setPrivacy] = useState(null);
  const [showAllEquip, setShowAllEquip] = useState(false);
  // Interest form
  const [formName, setFormName] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formMsg, setFormMsg] = useState("");
  const [formAccepted, setFormAccepted] = useState(false);
  const [formCommercial, setFormCommercial] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSent, setFormSent] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/vehicles/${id}`).then(r => r.data),
      axios.get(`${API}/api/config/privacy`).then(r => r.data).catch(() => null),
    ]).then(([veh, priv]) => {
      setV(veh);
      setPrivacy(priv);
    }).catch(() => toast.error("Vehículo no encontrado")).finally(() => setLoading(false));
  }, [id]);

  const getThumbUrl = useCallback((idx) => {
    if (!v?.images?.length) return null;
    const img = v.images[idx];
    if (!img?.path) return null;
    return `${API}/api/files/${img.path}`;
  }, [v]);

  const mainIdx = v ? (v.main_image_index ?? 0) : 0;
  const fallbackMain = v?.main_image_path ? `${API}/api/files/${v.main_image_path}` : null;
  const mainImg = v ? (getThumbUrl(mainIdx) || fallbackMain || v.hero_image_url || v.image_urls?.[0] || null) : null;

  const submitInterest = async () => {
    if (!formName || !formPhone) { toast.error("Nombre y teléfono son obligatorios"); return; }
    if (!formAccepted) { setFormError("Debes aceptar la politica de privacidad"); return; }
    setFormError(null);
    try {
      await axios.post(`${API}/api/public/request-attention`, {
        client_name: formName, client_phone: formPhone, client_email: formEmail,
        plate: v?.plate || "", message: `Interes en: ${v?.brand} ${v?.model} ${v?.version || ""} (Ref: ${v?.stock_reference || v?.id}). ${formMsg}`,
      });
      setFormSent(true);
      toast.success("Solicitud enviada correctamente");
    } catch { toast.error("Error al enviar"); }
  };

  const openWA = (phone, name) => {
    const num = phone.replace(/[^0-9]/g, "");
    const fullNum = num.length === 9 ? `34${num}` : num;
    const text = `Hola ${name}, estoy interesado en el ${v?.brand} ${v?.model} ${v?.version || ""} (Ref: ${v?.stock_reference || ""}).`;
    window.open(`https://wa.me/${fullNum}?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (loading) return (
    <div className="py-20 text-center">
      <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full mx-auto" />
    </div>
  );
  if (!v) return (
    <div className="py-20 text-center">
      <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
      <h2 className="heading-industrial text-xl font-bold text-slate-400 uppercase">Vehiculo no encontrado</h2>
      <Link to="/vehiculos" className="text-sm text-red-600 hover:underline mt-2 inline-block">Ver catalogo</Link>
    </div>
  );

  const img = mainImg;
  const title = `${v.brand} ${v.model}`;
  const fullTitle = `${title}${v.version ? " " + v.version : ""}${v.trim ? " " + v.trim : ""}`;
  const stockType = v.stock_type || v.vehicle_type || "usado";
  const priceCash = v.price || 0;
  const priceFinanced = v.price_financed || 0;
  const monthlyPayment = v.monthly_payment_from || 0;
  const allEquip = [...(v.featured_equipment || []), ...(v.full_equipment || [])];
  const categorized = categorizeEquipment(allEquip);
  const hasEquip = allEquip.length > 0;
  const desc = v.long_description || v.description || v.short_description || "";
  const seller = v.seller;
  const center = v.center;

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="vehicle-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Link to="/vehiculos" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors" data-testid="detail-back-link">
            <ChevronLeft className="w-3 h-3" /> Volver al catalogo
          </Link>
        </div>
      </div>

      {/* === HERO === */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left: Image */}
            <div>
              <div className="relative bg-slate-100 rounded-sm overflow-hidden aspect-[4/3] group cursor-pointer" onClick={() => img && setLightbox(true)} data-testid="detail-hero-image">
                {img ? (
                  <img src={img} alt={fullTitle} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Car className="w-24 h-24 text-slate-300" /></div>
                )}
                {img && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                  </div>
                )}
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                  <Badge className={`${STOCK_COLORS[stockType] || "bg-slate-700"} text-white rounded-sm text-xs uppercase font-bold px-2.5 py-1`}>
                    {STOCK_LABELS[stockType] || stockType}
                  </Badge>
                  {v.status === "disponible" && <Badge className="bg-emerald-600 text-white rounded-sm text-xs uppercase font-bold px-2.5 py-1">Disponible</Badge>}
                  {v.status === "reservado" && <Badge className="bg-amber-600 text-white rounded-sm text-xs uppercase font-bold px-2.5 py-1">Reservado</Badge>}
                  {v.vat_deductible && <Badge className="bg-blue-600 text-white rounded-sm text-xs uppercase font-bold px-2.5 py-1">IVA deducible</Badge>}
                </div>
              </div>
              {/* Thumbnail gallery */}
              <div className="grid grid-cols-5 gap-2 mt-2" data-testid="detail-thumbnails">
                {Array.from({length: Math.max(5, v.images?.length || 0)}, (_, i) => {
                  const tImg = getThumbUrl(i);
                  const isMain = i === mainIdx;
                  return (
                    <div key={i} className={`aspect-[4/3] rounded-sm overflow-hidden border-2 ${isMain ? "border-red-500" : "border-slate-200"} ${tImg ? "cursor-pointer" : ""}`}
                      onClick={() => tImg && setLightbox(true)}>
                      {tImg ? (
                        <img src={tImg} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                          <Car className="w-5 h-5 text-slate-300" />
                        </div>
                      )}
                    </div>
                  );
                }).slice(0, Math.max(5, v.images?.length || 0))}
              </div>
            </div>

            {/* Right: Info */}
            <div className="flex flex-col">
              {/* Title */}
              <div className="mb-4">
                {v.stock_reference && <span className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">REF: {v.stock_reference}</span>}
                <h1 className="heading-industrial text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase tracking-tight leading-tight" data-testid="detail-title">
                  {fullTitle}
                </h1>
                {v.short_description && <p className="text-sm text-slate-500 mt-1">{v.short_description}</p>}
              </div>

              {/* Price */}
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-4" data-testid="detail-price-block">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-3xl sm:text-4xl font-extrabold text-red-600 font-mono" data-testid="detail-price">
                    {priceCash.toLocaleString("es-ES")} &euro;
                  </span>
                  {priceFinanced > 0 && (
                    <span className="text-lg text-slate-500 line-through font-mono">{priceFinanced.toLocaleString("es-ES")} &euro; financiado</span>
                  )}
                </div>
                {monthlyPayment > 0 && (
                  <p className="text-sm text-slate-600 mt-1">
                    Desde <span className="font-bold text-slate-900">{monthlyPayment.toLocaleString("es-ES")} &euro;/mes</span>
                    {v.finance_entry > 0 && <span className="text-slate-400"> | Entrada {v.finance_entry.toLocaleString("es-ES")} &euro;</span>}
                    {v.finance_term_months > 0 && <span className="text-slate-400"> | {v.finance_term_months} meses</span>}
                  </p>
                )}
                {v.vat_deductible && <p className="text-xs text-blue-600 font-semibold mt-1">IVA deducible para empresas y autonomos</p>}
              </div>

              {/* Quick specs */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4" data-testid="detail-quick-specs">
                {v.year > 0 && <Spec icon={Calendar} label="Ano" value={v.registration_date || String(v.year)} />}
                {v.mileage >= 0 && <Spec icon={Gauge} label="Km" value={`${v.mileage.toLocaleString("es-ES")} km`} />}
                {v.fuel_type && <Spec icon={Fuel} label="Combustible" value={v.fuel_type} />}
                {v.transmission && <Spec icon={Settings2} label="Cambio" value={v.transmission} />}
                {(v.power_cv > 0 || v.power) && <Spec icon={Zap} label="Potencia" value={v.power_cv > 0 ? `${v.power_cv} CV` : v.power} />}
                {v.eco_label && <Spec icon={Leaf} label="Etiqueta" value={ECO_LABELS[v.eco_label] || v.eco_label} badge={ECO_COLORS[v.eco_label]} />}
                {v.body_type && <Spec icon={Car} label="Carroceria" value={v.body_type} />}
                {v.color && <Spec icon={Palette} label="Color" value={v.color} />}
              </div>

              {/* Location */}
              {center && (
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
                  <MapPin className="w-3.5 h-3.5 text-red-500" />
                  <span>{center.name} - {center.address}, {center.city}</span>
                </div>
              )}

              {/* Trust badges */}
              <div className="flex flex-wrap gap-2 mb-5" data-testid="detail-trust-badges">
                {v.warranty_months > 0 && <TrustBadge icon={Shield} text={`${v.warranty_months} meses garantia`} />}
                {v.certified_mileage && <TrustBadge icon={CheckCircle} text="Km certificado" />}
                {v.structural_damage_free && <TrustBadge icon={CheckCircle} text="Sin danos estructurales" />}
                {v.delivery_available && <TrustBadge icon={Package} text="Entrega a domicilio" />}
                {v.trade_in_available && <TrustBadge icon={ArrowRight} text="Aceptamos tu coche" />}
                {v.inspection_points > 0 && <TrustBadge icon={Star} text={`Revisado ${v.inspection_points} puntos`} />}
                {v.maintenance_history_available && <TrustBadge icon={FileText} text="Historial disponible" />}
              </div>

              {/* CTAs */}
              <div className="space-y-2 mt-auto" data-testid="detail-ctas">
                <Button onClick={() => document.getElementById("interest-form")?.scrollIntoView({behavior: "smooth"})}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm h-12 text-sm uppercase tracking-wider font-bold" data-testid="cta-interest">
                  Me interesa
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => document.getElementById("interest-form")?.scrollIntoView({behavior: "smooth"})}
                    className="rounded-sm h-10 text-xs uppercase tracking-wider font-semibold border-slate-300" data-testid="cta-reserve">
                    Reservar vehiculo
                  </Button>
                  <Button variant="outline" onClick={() => document.getElementById("finance-section")?.scrollIntoView({behavior: "smooth"})}
                    className="rounded-sm h-10 text-xs uppercase tracking-wider font-semibold border-slate-300" data-testid="cta-finance">
                    Financiacion
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {seller?.whatsapp_phone && (
                    <Button onClick={() => openWA(seller.whatsapp_phone, seller.name)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-10 text-xs uppercase tracking-wider font-bold" data-testid="cta-whatsapp">
                      <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
                    </Button>
                  )}
                  {(center?.phone || seller?.phone) && (
                    <Button variant="outline" onClick={() => window.location.href = `tel:${center?.phone || seller?.phone}`}
                      className="rounded-sm h-10 text-xs uppercase tracking-wider font-semibold border-slate-300" data-testid="cta-call">
                      <Phone className="w-3.5 h-3.5 mr-1.5" /> Llamar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === CONTENT SECTIONS === */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 space-y-8 lg:space-y-12">

        {/* Specs grid */}
        <SectionBlock title="Especificaciones tecnicas" icon={Cog} testId="specs-section">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {v.registration_date && <SpecCard icon={Calendar} label="Matriculacion" value={v.registration_date} />}
            {!v.registration_date && v.year > 0 && <SpecCard icon={Calendar} label="Ano" value={String(v.year)} />}
            <SpecCard icon={Gauge} label="Kilometraje" value={`${(v.mileage || 0).toLocaleString("es-ES")} km`} />
            {v.fuel_type && <SpecCard icon={Fuel} label="Combustible" value={v.fuel_type} />}
            {v.transmission && <SpecCard icon={Settings2} label="Transmision" value={v.transmission} />}
            {(v.power_cv > 0 || v.power) && <SpecCard icon={Zap} label="Potencia" value={v.power_cv > 0 ? `${v.power_cv} CV` : v.power} />}
            {v.eco_label && <SpecCard icon={Leaf} label="Etiqueta ambiental" value={ECO_LABELS[v.eco_label] || v.eco_label} />}
            {v.body_type && <SpecCard icon={Car} label="Carroceria" value={v.body_type} />}
            {v.color && <SpecCard icon={Palette} label="Color exterior" value={v.color} />}
            {v.color_interior && <SpecCard icon={Palette} label="Color interior" value={v.color_interior} />}
            {v.doors > 0 && <SpecCard icon={DoorOpen} label="Puertas" value={String(v.doors)} />}
            {v.seats > 0 && <SpecCard icon={Users} label="Plazas" value={String(v.seats)} />}
            {v.traction && <SpecCard icon={Cog} label="Traccion" value={v.traction} />}
            {v.displacement_cc > 0 && <SpecCard icon={Cog} label="Cilindrada" value={`${v.displacement_cc} cc`} />}
            {v.consumption_mixed && <SpecCard icon={Fuel} label="Consumo mixto" value={v.consumption_mixed} />}
            {v.co2_emissions && <SpecCard icon={Leaf} label="CO2" value={v.co2_emissions} />}
            {v.boot_capacity && <SpecCard icon={Package} label="Maletero" value={v.boot_capacity} />}
          </div>
        </SectionBlock>

        {/* Trust & certification */}
        <SectionBlock title="Garantia y certificacion" icon={Shield} testId="trust-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {v.warranty_months > 0 && (
              <TrustCard icon={Shield} title={`${v.warranty_months} meses de garantia`} desc={v.warranty_extendable ? "Ampliable a 24 o 36 meses" : "Garantia incluida en el precio"} />
            )}
            {v.inspection_points > 0 && (
              <TrustCard icon={CheckCircle} title={`Revisado en ${v.inspection_points} puntos`} desc="Inspeccion tecnica completa por nuestros tecnicos certificados" />
            )}
            {v.certified_mileage && (
              <TrustCard icon={Gauge} title="Kilometraje certificado" desc="Verificado y garantizado por Ascauto" />
            )}
            {v.structural_damage_free && (
              <TrustCard icon={CheckCircle} title="Sin danos estructurales" desc="Comprobacion de carroceria y chasis incluida" />
            )}
            {v.maintenance_history_available && (
              <TrustCard icon={FileText} title="Historial de mantenimiento" desc="Libro de revisiones disponible para consulta" />
            )}
            {v.delivery_available && (
              <TrustCard icon={Package} title="Entrega a domicilio" desc="Te llevamos el vehiculo a donde tu nos digas" />
            )}
            {v.trade_in_available && (
              <TrustCard icon={ArrowRight} title="Aceptamos tu coche" desc="Tasamos tu vehiculo actual como parte de pago" />
            )}
          </div>
        </SectionBlock>

        {/* Equipment */}
        {hasEquip && (
          <SectionBlock title="Equipamiento" icon={Star} testId="equipment-section">
            {v.featured_equipment?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">Equipamiento destacado</h3>
                <div className="flex flex-wrap gap-2">
                  {v.featured_equipment.map((e, i) => (
                    <span key={i} className="bg-red-50 text-red-700 border border-red-200 rounded-sm px-3 py-1.5 text-xs font-medium">{e}</span>
                  ))}
                </div>
              </div>
            )}
            {Object.entries(categorized).length > 0 && (
              <div className={showAllEquip ? "" : "max-h-64 overflow-hidden relative"}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(categorized).map(([cat, items]) => (
                    <div key={cat}>
                      <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">{cat}</h4>
                      <ul className="space-y-1">
                        {items.map((item, i) => (
                          <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" /> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                {!showAllEquip && (
                  <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent" />
                )}
              </div>
            )}
            {allEquip.length > 8 && (
              <button onClick={() => setShowAllEquip(!showAllEquip)} className="text-xs text-red-600 hover:text-red-700 font-semibold uppercase tracking-wider mt-3" data-testid="equip-toggle">
                {showAllEquip ? "Ver menos" : `Ver todo el equipamiento (${allEquip.length})`}
              </button>
            )}
          </SectionBlock>
        )}

        {/* Description */}
        {desc && (
          <SectionBlock title="Descripcion" icon={FileText} testId="description-section">
            <div className="prose prose-sm prose-slate max-w-none text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-testid="detail-description">
              {desc}
            </div>
          </SectionBlock>
        )}

        {/* Financing */}
        <div id="finance-section">
          <SectionBlock title="Financiacion" icon={Cog} testId="finance-section-block">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Precio al contado</p>
                  <p className="text-2xl font-extrabold text-slate-900 font-mono">{priceCash.toLocaleString("es-ES")} &euro;</p>
                </div>
                {monthlyPayment > 0 && (
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Cuota desde</p>
                    <p className="text-2xl font-extrabold text-red-600 font-mono">{monthlyPayment.toLocaleString("es-ES")} &euro;/mes</p>
                  </div>
                )}
                {v.finance_entry > 0 && (
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Entrada</p>
                    <p className="text-2xl font-extrabold text-slate-700 font-mono">{v.finance_entry.toLocaleString("es-ES")} &euro;</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400 text-center mb-4">* Cuota orientativa. Sujeta a aprobacion por la entidad financiera. Consulta condiciones sin compromiso.</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button onClick={() => document.getElementById("interest-form")?.scrollIntoView({behavior: "smooth"})}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-10 px-6 text-xs uppercase tracking-wider font-bold" data-testid="finance-cta">
                  Solicitar estudio de financiacion
                </Button>
                {seller?.whatsapp_phone && (
                  <Button variant="outline" onClick={() => openWA(seller.whatsapp_phone, seller.name)}
                    className="rounded-sm h-10 px-6 text-xs uppercase tracking-wider font-semibold border-slate-300">
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Hablar con comercial
                  </Button>
                )}
              </div>
            </div>
          </SectionBlock>
        </div>

        {/* Advisor + Contact + Interest form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Advisor */}
          <div className="lg:col-span-1 space-y-4">
            {seller && (
              <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="seller-block">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-4">Tu comercial</h3>
                <div className="flex items-center gap-3 mb-4">
                  <img src={getImageUrl(seller.avatar_url)} alt={seller.name} className="w-14 h-14 rounded-full object-cover border-2 border-slate-200" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">{seller.name}</p>
                    <p className="text-xs text-slate-500">{seller.title}</p>
                    {seller.center && <p className="text-xs text-slate-400">{seller.center}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  {seller.phone && (
                    <a href={`tel:${seller.phone}`} className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 transition-colors">
                      <Phone className="w-3.5 h-3.5 text-red-500" /> {seller.phone}
                    </a>
                  )}
                  {seller.whatsapp_phone && (
                    <button onClick={() => openWA(seller.whatsapp_phone, seller.name)}
                      className="flex items-center gap-2 text-xs text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-testid="seller-wa-btn">
                      <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Center */}
            {center && (
              <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="center-block">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">Centro</h3>
                <p className="text-sm font-semibold text-slate-900">{center.name}</p>
                <p className="text-xs text-slate-500 mt-1">{center.address}</p>
                <p className="text-xs text-slate-500">{center.zip_code} {center.city}</p>
                {center.phone && (
                  <a href={`tel:${center.phone}`} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 mt-2 transition-colors">
                    <Phone className="w-3 h-3 text-red-500" /> {center.phone}
                  </a>
                )}
                {center.working_hours && (
                  <div className="mt-2 text-xs text-slate-400">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {center.working_hours.lunes && center.working_hours.lunes !== "cerrado" ? `L-V: ${center.working_hours.lunes}` : ""}
                  </div>
                )}
              </div>
            )}

            {/* Trade in / Delivery */}
            <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-3" data-testid="services-block">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Servicios</h3>
              <ServiceRow icon={ArrowRight} text="Tasamos tu coche. Aceptamos tu vehiculo actual como parte de pago." />
              <ServiceRow icon={Package} text="Entrega a domicilio disponible en toda la peninsula." />
              <ServiceRow icon={Shield} text="Todos nuestros vehiculos con garantia incluida." />
            </div>
          </div>

          {/* Interest form */}
          <div className="lg:col-span-2" id="interest-form">
            <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="interest-form-block">
              <h3 className="heading-industrial text-lg font-bold text-slate-900 uppercase tracking-tight mb-1">Me interesa este vehiculo</h3>
              <p className="text-xs text-slate-500 mb-5">Rellena el formulario y te contactamos sin compromiso.</p>

              {!formSent ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium uppercase tracking-wider">Nombre *</label>
                      <Input value={formName} onChange={e => setFormName(e.target.value)} className="rounded-sm h-10" data-testid="interest-name" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 font-medium uppercase tracking-wider">Telefono *</label>
                      <Input value={formPhone} onChange={e => setFormPhone(e.target.value)} className="rounded-sm h-10" data-testid="interest-phone" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-medium uppercase tracking-wider">Email</label>
                    <Input value={formEmail} onChange={e => setFormEmail(e.target.value)} className="rounded-sm h-10" data-testid="interest-email" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-medium uppercase tracking-wider">Mensaje (opcional)</label>
                    <textarea value={formMsg} onChange={e => setFormMsg(e.target.value)}
                      placeholder="Estoy interesado en este vehiculo. Me gustaria recibir mas informacion sobre financiacion / tasacion de mi coche actual / entrega..."
                      className="w-full border border-slate-200 rounded-sm p-3 text-sm h-24 resize-none" data-testid="interest-message" />
                  </div>

                  <PrivacyBlock
                    privacyText={privacy?.privacy_form_text_contact}
                    policyUrl={privacy?.privacy_policy_url || "/politica-de-privacidad"}
                    contactEmail={privacy?.privacy_contact_email}
                    checkboxText={privacy?.privacy_checkbox_text || "He leido y acepto la"}
                    accepted={formAccepted}
                    onAcceptedChange={v => { setFormAccepted(v); setFormError(null); }}
                    error={formError}
                    showCommercial={true}
                    commercialText={privacy?.privacy_commercial_text}
                    commercialAccepted={formCommercial}
                    onCommercialChange={setFormCommercial}
                  />

                  <Button onClick={submitInterest}
                    className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm h-12 text-sm uppercase tracking-wider font-bold" data-testid="interest-submit">
                    <Send className="w-4 h-4 mr-2" /> Enviar solicitud
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8" data-testid="interest-sent">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-slate-900">Solicitud enviada</h3>
                  <p className="text-sm text-slate-500 mt-1">Te contactaremos lo antes posible.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && img && (
        <div className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="lightbox">
          <button onClick={() => setLightbox(false)} className="absolute top-4 right-4 text-white/70 hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
          <img src={img} alt={fullTitle} className="max-w-full max-h-[90vh] object-contain rounded-sm shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function Spec({ icon: Icon, label, value, badge }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
      <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-slate-400 uppercase tracking-wider leading-none">{label}</p>
        <p className="text-xs font-semibold text-slate-900 truncate">{value}</p>
      </div>
      {badge && <div className={`w-2 h-2 rounded-full ${badge} ml-auto flex-shrink-0`} />}
    </div>
  );
}

function SpecCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
      <Icon className="w-5 h-5 text-red-500 mx-auto mb-1.5" />
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-sm font-bold text-slate-900">{value}</p>
    </div>
  );
}

function TrustBadge({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2.5 py-1 text-[11px] font-medium">
      <Icon className="w-3 h-3" /> {text}
    </span>
  );
}

function TrustCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-emerald-50 rounded-sm flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
        </div>
      </div>
    </div>
  );
}

function SectionBlock({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-red-600" />
        <h2 className="heading-industrial text-base sm:text-lg font-bold text-slate-900 uppercase tracking-tight">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function ServiceRow({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-6 h-6 bg-red-50 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-3 h-3 text-red-600" />
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}
